import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { getActiveTimer, startTimer, stopTimer } from "services/ProjectService";
import { AuthContext, getKeyDetails } from "context/AuthContext";

export const TimerContext = createContext();

export const TimerProvider = ({ children }) => {
  const authUser = useContext(AuthContext);
  const userId = getKeyDetails(authUser, "userId");
  
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  // Fetch active timer on mount and when userId changes
  useEffect(() => {
    if (userId) {
      fetchActiveTimer();
      
      // Periodically refresh active timer state (every 30 seconds) to ensure sync
      const refreshInterval = setInterval(() => {
        fetchActiveTimer();
      }, 30000);
      
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
        clearInterval(refreshInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Update elapsed time every second when timer is active
  useEffect(() => {
    if (activeTimer && activeTimer.status === "running") {
      // Calculate initial elapsed time
      const startTime = new Date(activeTimer.startTime);
      const now = new Date();
      const initialElapsed = Math.floor((now - startTime) / 1000);
      setElapsedSeconds(initialElapsed);

      // Update every second
      const interval = setInterval(() => {
        const currentTime = new Date();
        const elapsed = Math.floor((currentTime - startTime) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);

      setIntervalId(interval);
      return () => clearInterval(interval);
    } else {
      setElapsedSeconds(0);
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimer]);

  const fetchActiveTimer = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await getActiveTimer();
      if (response.success && response.data?.activeTimer) {
        setActiveTimer(response.data.activeTimer);
      } else {
        setActiveTimer(null);
        setElapsedSeconds(0);
      }
    } catch (error) {
      console.error("Error fetching active timer:", error);
      setActiveTimer(null);
      setElapsedSeconds(0);
    }
  }, [userId]);

  const handleStartTimer = useCallback(async (taskId) => {
    if (!userId) return { success: false, msg: ["User not authenticated"] };
    
    setLoading(true);
    try {
      const response = await startTimer(taskId);
      if (response.success) {
        setActiveTimer(response.data?.timeLog);
        await fetchActiveTimer(); // Refresh to get latest state
        return { success: true, data: response.data };
      } else {
        return { success: false, msg: response.msg || ["Failed to start timer"] };
      }
    } catch (error) {
      console.error("Error starting timer:", error);
      return { success: false, msg: [error.message || "Error starting timer"] };
    } finally {
      setLoading(false);
    }
  }, [userId, fetchActiveTimer]);

  const handleStopTimer = useCallback(async (timeLogId) => {
    if (!userId || !timeLogId) return { success: false, msg: ["Invalid timer"] };
    
    setLoading(true);
    try {
      const response = await stopTimer(timeLogId);
      if (response.success) {
        setActiveTimer(null);
        setElapsedSeconds(0);
        await fetchActiveTimer(); // Refresh to get latest state
        return { success: true, data: response.data };
      } else {
        return { success: false, msg: response.msg || ["Failed to stop timer"] };
      }
    } catch (error) {
      console.error("Error stopping timer:", error);
      return { success: false, msg: [error.message || "Error stopping timer"] };
    } finally {
      setLoading(false);
    }
  }, [userId, fetchActiveTimer]);

  const formatTime = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const value = {
    activeTimer,
    elapsedSeconds,
    loading,
    formatTime,
    startTimer: handleStartTimer,
    stopTimer: handleStopTimer,
    refreshTimer: fetchActiveTimer,
    isTaskTimerRunning: (taskId) => {
      return activeTimer?.taskId?._id === taskId || activeTimer?.taskId?.toString() === taskId?.toString();
    },
  };

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
};

